import React, { useCallback, useEffect, useMemo, useState } from "react";

// Modules
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

// App
import { serverUrl } from "../../config";
import CompanyappLoader from "../../partials/companyappLoader";
import Error from "../../partials/error";
import ClientTheme from "../../core/clientTheme";
import { isCompanyappUser } from "../../utils/helpers";
import {
  readDiningIntro,
  readFdFeatures,
  readFigures,
  readHotelGalleryCategories,
  readHotelServices,
  readIdentity,
  readImmersiveTour,
  readLocation,
  readMeetingFeatures,
  readMeetingGallery,
  readMeetingRooms,
  readOutlets,
  readPickedImages,
  readRoomFeatures,
  readRooms,
  readUsps,
} from "./hotelData";
import HotelPicker from "./partials/hotelPicker";
import CapacitySection from "./partials/capacitySection";
import RoomsSection from "./partials/roomsSection";
import DiningSection from "./partials/diningSection";
import FeaturesSection from "./partials/featuresSection";
import GallerySection from "./partials/gallerySection";
import LocationSection from "./partials/locationSection";
import MapSection from "./partials/mapSection";
import { ApiPath, Field, Notice, Section } from "./partials/apiField";

// UI components
import { AlertCircle, ExternalLink } from "react-feather";

const NAV = [
  { id: "overview", label: "Overview" },
  { id: "figures", label: "Key figures" },
  { id: "rooms", label: "Guest rooms" },
  { id: "location", label: "Location" },
  { id: "map", label: "Map" },
  { id: "capacity", label: "Capacity" },
  { id: "features", label: "Meeting features" },
  { id: "services", label: "Hotel services" },
  { id: "dining", label: "Food & beverage" },
  { id: "gallery", label: "Gallery" },
  { id: "raw", label: "Raw response" },
];

// The theme label varies per client and the key is the same across them, so
// the first theme that carries one wins rather than hardcoding a label.
const findGoogleApiKey = (themes) => {
  if (!themes) return "";
  const values = Array.isArray(themes) ? themes : Object.values(themes);
  for (const entry of values) {
    try {
      const json = typeof entry === "string" ? JSON.parse(entry) : entry;
      if (json?.googleApiKey) return json.googleApiKey;
    } catch (error) {
      // A theme that does not parse simply carries no key.
    }
  }
  return "";
};

export default function HotelProfile() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get("code") || "");
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const user = useSelector((state) => state.authReducer.user);
  const themes = useSelector((state) => state.themesReducer.themes);
  const clientTheme = { ...ClientTheme() };

  const apiKey = useMemo(() => findGoogleApiKey(themes), [themes]);
  const brandColor = clientTheme?.color?.brand?.primary || "#c00d0d";

  // Backstop: the route is only registered for this account (see routes.js),
  // so this guard only fires if the screen is ever mounted another way.
  const isAllowed = isCompanyappUser(user);

  const load = useCallback((nextCode) => {
    if (!nextCode) return;

    setIsLoading(true);
    setLoadError("");

    fetch(
      `${serverUrl}/api/v2/rhg/hotel-detail?code=${encodeURIComponent(
        nextCode,
      )}&_format=json`,
      { credentials: "include" },
    )
      .then(async (response) => {
        const json = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(json?.error || `HTTP ${response.status}`);
        }
        return json;
      })
      .then((json) => {
        setPayload(json);
        setIsLoading(false);
      })
      .catch((error) => {
        setPayload(null);
        setLoadError(error.message);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isAllowed && code) load(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  const onSelect = (nextCode) => {
    setCode(nextCode);
    // Merge rather than replace: on localhost the client is read from
    // ?client=, and replacing the query string would drop it.
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set("code", nextCode);
      return next;
    });
    load(nextCode);
  };

  const model = useMemo(() => {
    if (!payload) return null;

    const meeting = readMeetingRooms(payload);
    return {
      identity: readIdentity(payload),
      figures: readFigures(payload),
      location: readLocation(payload),
      meeting,
      features: readMeetingFeatures(payload),
      services: readHotelServices(payload),
      outlets: readOutlets(payload),
      meetingGallery: readMeetingGallery(payload, meeting.rooms),
      hotelGalleries: readHotelGalleryCategories(payload),
      picked: readPickedImages(payload),
      tour: readImmersiveTour(payload),
      // Guest rooms (synced) plus the editorial copy and feature lists that
      // only exist in the imported spreadsheet.
      rooms: readRooms(payload),
      usps: readUsps(payload),
      roomFeatures: readRoomFeatures(payload),
      diningIntro: readDiningIntro(payload),
      fdFeatures: readFdFeatures(payload),
    };
  }, [payload]);

  if (!isAllowed) {
    return (
      <Error
        status={403}
        message="This screen is restricted to the companyapp account."
      />
    );
  }

  const partialErrors = [
    payload?.roomInfoError && `roomInfo: ${payload.roomInfoError}`,
    payload?.galleryError && `gallery: ${payload.galleryError}`,
    payload?.restaurantsError && `restaurants: ${payload.restaurantsError}`,
    payload?.roomsError && `rooms: ${payload.roomsError}`,
    payload?.roomGalleriesError &&
      `roomGalleries: ${payload.roomGalleriesError}`,
  ].filter(Boolean);

  return (
    <main className="hotel-profile screen">
      <div className="rhp-head">
        <h1>Hotel profile</h1>
        {code ? (
          <a
            className="rhp-head__raw"
            href={`${serverUrl}/api/v2/rhg/hotel-detail?code=${encodeURIComponent(
              code,
            )}&_format=json`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open raw endpoint <ExternalLink size={14} />
          </a>
        ) : null}
      </div>

      <HotelPicker
        onSelect={onSelect}
        selectedCode={code}
        selectedName={model?.identity?.name || ""}
        isLoading={isLoading}
      />

      {isLoading ? <CompanyappLoader /> : null}

      {loadError ? (
        <div className="rhp-error">
          <AlertCircle size={18} />
          <div>
            <strong>Could not load {code}</strong>
            <span>{loadError}</span>
          </div>
        </div>
      ) : null}

      {!isLoading && !loadError && !model ? (
        <div className="rhp-placeholder">No hotel selected</div>
      ) : null}

      {!isLoading && model ? (
        <>
          <nav className="rhp-nav">
            {NAV.map((item) => (
              <a key={item.id} href={`#${item.id}`}>
                {item.label}
              </a>
            ))}
          </nav>

          {payload?.hotel == null ? (
            <Notice tone="warn">
              <code>hotel</code> is <code>null</code> for {code}: not synced.
              Live sections below still load.
            </Notice>
          ) : null}

          {partialErrors.length ? (
            <Notice tone="warn">{partialErrors.join(" · ")}</Notice>
          ) : null}

          <Section
            id="overview"
            title={model.identity.name || code}
            subtitle={[
              model.identity.brand,
              model.identity.city,
              model.identity.country,
            ]
              .filter(Boolean)
              .join(" · ")}
            path="hotel"
            raw={payload?.hotel}
          >
            <div
              className={`rhp-overview${
                model.identity.heroImage ? "" : " rhp-overview--no-media"
              }`}
            >
              {model.identity.heroImage ? (
                <div className="rhp-overview__media">
                  <img
                    src={model.identity.heroImage}
                    alt={model.identity.name}
                  />
                  <ApiPath path={model.identity.heroImagePath} />
                </div>
              ) : null}

              <div className="rhp-overview__body">
                {model.identity.title ? (
                  <h3>{model.identity.title}</h3>
                ) : null}
                {model.identity.description ? (
                  <p>{model.identity.description}</p>
                ) : null}

                <div className="rhp-grid rhp-grid--tight">
                  <Field
                    label="EMMA ID"
                    value={model.identity.code}
                    path={model.identity.codePath}
                  />
                  <Field
                    label="Brand"
                    value={model.identity.brand}
                    path={model.identity.brandPath}
                  />
                  <Field
                    label="Star rating"
                    value={model.identity.stars}
                    suffix={model.identity.stars ? "star" : ""}
                    path={model.identity.starsPath}
                  />
                  <Field
                    label="Status"
                    value={model.identity.status}
                    path={model.identity.statusPath}
                  />
                  <Field
                    label="Hotel type"
                    value={model.identity.hotelTypes.join(", ")}
                    path={model.identity.hotelTypesPath}
                  />
                  <Field
                    label="Last published"
                    value={model.identity.lastPublishDate}
                    path={model.identity.lastPublishDatePath}
                  />
                </div>
              </div>
            </div>

            {model.usps.length ? (
              <>
                <h3 className="rhp-subhead">
                  Unique selling points
                  <span className="rhp-badge rhp-badge--imported">
                    Imported
                  </span>
                </h3>
                <div className="rhp-usps">
                  {model.usps.map((usp) => (
                    <article className="rhp-usp" key={usp.index}>
                      <h4>{usp.title}</h4>
                      {usp.subtitle ? (
                        <p className="rhp-usp__subtitle">{usp.subtitle}</p>
                      ) : null}
                      {usp.description ? (
                        <p className="rhp-usp__description">
                          {usp.description}
                        </p>
                      ) : null}
                      {usp.label && usp.label !== usp.title ? (
                        <span className="rhp-usp__label">{usp.label}</span>
                      ) : null}
                      <ApiPath path={usp.path} />
                    </article>
                  ))}
                </div>
              </>
            ) : null}

            {model.picked.length ? (
              <>
                <h3 className="rhp-subhead">ICE Portal selection</h3>
                <div className="rhp-picked">
                  {model.picked.map((image) => (
                    <figure key={image.slot}>
                      <img src={image.thumb || image.url} alt={image.slot} />
                      <figcaption>
                        <strong>{image.slot}</strong>
                        <span>{image.category}</span>
                        <ApiPath path={image.path} />
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </>
            ) : null}
          </Section>

          <Section
            id="figures"
            title="Key figures"
            path="roomInfo.collection[0].content"
            raw={payload?.roomInfo}
          >
            <div className="rhp-figures">
              {model.figures.map((figure) => (
                <div
                  className={`rhp-figure${figure.value ? "" : " is-empty"}`}
                  key={figure.key}
                >
                  <span className="rhp-figure__value">
                    {figure.value || "—"}
                    {figure.value && figure.suffix ? (
                      <small>{figure.suffix}</small>
                    ) : null}
                  </span>
                  <span className="rhp-figure__label">{figure.label}</span>
                  <ApiPath path={figure.path} />
                </div>
              ))}
            </div>
          </Section>

          <RoomsSection
            rooms={model.rooms}
            features={model.roomFeatures}
            raw={payload?.rooms}
          />

          <LocationSection
            location={model.location}
            identity={model.identity}
            raw={payload?.hotel?.content?.contactInfo}
          />

          <MapSection
            location={model.location}
            identity={model.identity}
            apiKey={apiKey}
          />

          <CapacitySection
            meeting={model.meeting}
            brandColor={brandColor}
            raw={payload?.roomInfo?.collection?.[0]?.content?.meetingRooms}
          />

          <FeaturesSection
            features={model.features}
            services={model.services}
            roomFeatures={model.roomFeatures}
            raw={payload?.roomInfo?.collection?.[0]?.content?.features}
          />

          <DiningSection
            outlets={model.outlets}
            intro={model.diningIntro}
            fdFeatures={model.fdFeatures}
            raw={payload?.restaurants}
          />

          <GallerySection
            meetingGallery={model.meetingGallery}
            hotelGalleries={model.hotelGalleries}
            tour={model.tour}
            raw={payload?.gallery}
          />

          <Section
            id="raw"
            title="Raw response"
            path="/api/v2/rhg/hotel-detail?code="
            raw={payload}
          />
        </>
      ) : null}
    </main>
  );
}
